import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { FilterableTable } from "./FilterableTable";
import type { ColumnSpec } from "./FilterableTable";
import { downloadCsvRowArrays } from "./csv";
import type { Filters } from "./filters";

// Modal shown when the user clicks the info button next to a dashboard's
// title. Tabs:
//   1. Schema  — schema-graph view of the tables this page touches.
//   2. Explain — plain-language description of each CTE / stage in the query.
//   3. SQL     — formatted source SQL (one or more queries).
//   4. Raw rows — full query output as a sortable, per-column-filterable
//      scrollable table, with CSV export.

const MAX_RENDER_ROWS = 2000;

type QueryResult = {
  columns: string[];
  rows: unknown[][];
  rowCount: number;
};

export type SqlQuery = { label: string; sql: string };

type Tab = "schema" | "explain" | "sql" | "raw";

const SQL_KEYWORDS = new Set([
  "WITH","AS","SELECT","FROM","WHERE","AND","OR","NOT","IN","ON","JOIN","LEFT","RIGHT",
  "INNER","OUTER","CROSS","LATERAL","GROUP","BY","ORDER","HAVING","LIMIT","OFFSET",
  "DISTINCT","UNION","ALL","CASE","WHEN","THEN","ELSE","END","FILTER","OVER","PARTITION",
  "BETWEEN","INTERVAL","EXTRACT","EPOCH","COALESCE","TRUE","FALSE","NULL","IS","ILIKE",
  "LIKE","ANY","ARRAY","UNNEST","TRIM","LOWER","UPPER","COUNT","SUM","MIN","MAX","AVG",
  "BOOL_OR","ARRAY_AGG","DATE_TRUNC","TO_CHAR","CAST","ASC","DESC","NULLS","FIRST","LAST",
]);

function highlightSql(sql: string): ReactNode[] {
  // Lightweight tokenizer: comments, strings, numbers, identifiers/keywords.
  const out: ReactNode[] = [];
  const re = /(--[^\n]*|\/\*[\s\S]*?\*\/)|('(?:''|[^'])*')|(\b\d+(?:\.\d+)?\b)|([A-Za-z_][A-Za-z_0-9]*)|([\s\S])/g;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(sql)) !== null) {
    const [tok, comment, str, num, ident, other] = m;
    if (comment) {
      out.push(<span key={key++} style={{ color: "#7a8475", fontStyle: "italic" }}>{tok}</span>);
    } else if (str) {
      out.push(<span key={key++} style={{ color: "#a85d3a" }}>{tok}</span>);
    } else if (num) {
      out.push(<span key={key++} style={{ color: "#3a6ea5" }}>{tok}</span>);
    } else if (ident) {
      const upper = ident.toUpperCase();
      if (SQL_KEYWORDS.has(upper)) {
        out.push(<span key={key++} style={{ color: "#1f6f7e", fontWeight: 600 }}>{tok}</span>);
      } else {
        out.push(<span key={key++}>{tok}</span>);
      }
    } else if (other) {
      out.push(other);
    }
  }
  return out;
}

function SqlBlock({ sql }: { sql: string }) {
  const trimmed = sql.replace(/^\n+/, "").replace(/\n+$/, "");
  return (
    <pre style={{
      margin: 0,
      padding: 14,
      background: "#FAF9F4",
      border: "1px solid var(--border-default)",
      borderRadius: 8,
      fontSize: 12,
      lineHeight: 1.55,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, monospace",
      whiteSpace: "pre",
      overflowX: "auto",
      color: "var(--fg-1)",
    }}>
      {highlightSql(trimmed)}
    </pre>
  );
}

export function QueryInspector({
  title,
  open,
  onClose,
  schemaContent,
  explainContent,
  queryResult,
  csvFilenamePrefix,
  filters,
  sqlQueries,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  schemaContent: ReactNode;
  explainContent: ReactNode;
  queryResult: QueryResult | null;
  csvFilenamePrefix: string;
  filters?: Filters | null;
  sqlQueries?: SqlQuery[];
}) {
  const [tab, setTab] = useState<Tab>("schema");
  const [sqlIndex, setSqlIndex] = useState(0);

  const rawColumns = useMemo<ColumnSpec<Record<string, unknown>>[]>(() => {
    if (!queryResult) return [];
    return queryResult.columns.map((c) => ({
      key: c,
      label: c,
      filterable: false,
      sortable: true,
      render: (_row, v) => {
        if (v == null) return <span style={{ color: "var(--fg-3)" }}>—</span>;
        if (typeof v === "boolean") return v ? "true" : "false";
        if (Array.isArray(v) || typeof v === "object") return JSON.stringify(v);
        return String(v);
      },
    }));
  }, [queryResult]);

  const rawRows = useMemo<Record<string, unknown>[]>(() => {
    if (!queryResult) return [];
    const slice = queryResult.rows.slice(0, MAX_RENDER_ROWS);
    return slice.map((row) => {
      const obj: Record<string, unknown> = {};
      queryResult.columns.forEach((c, i) => { obj[c] = row[i]; });
      return obj;
    });
  }, [queryResult]);

  if (!open) return null;
  const truncated = queryResult && queryResult.rows.length > MAX_RENDER_ROWS;
  const tabs: { key: Tab; label: string; show: boolean }[] = [
    { key: "schema", label: "Schema", show: true },
    { key: "explain", label: "Explain", show: true },
    { key: "sql", label: "SQL", show: !!sqlQueries && sqlQueries.length > 0 },
    { key: "raw", label: "Raw rows", show: true },
  ];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${title} — query inspector`}
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(20,20,18,0.45)", zIndex: 100,
        display: "flex", alignItems: "stretch", justifyContent: "center", padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-elevated, #fff)", borderRadius: 10, width: "100%",
          maxWidth: 1400, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden",
          boxShadow: "0 12px 48px rgba(0,0,0,0.25)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid var(--border-default)" }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>{title}</h2>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--fg-2)", fontSize: 18 }}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div role="tablist" style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--border-default)", padding: "0 8px" }}>
          {tabs.filter((t) => t.show).map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              style={{
                background: "transparent",
                border: "none",
                padding: "10px 14px",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: tab === t.key ? 600 : 400,
                color: tab === t.key ? "var(--fg-1)" : "var(--fg-3)",
                borderBottom: tab === t.key ? "2px solid var(--color-teal)" : "2px solid transparent",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 16, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {tab === "schema" && <div>{schemaContent}</div>}
          {tab === "explain" && <div>{explainContent}</div>}
          {tab === "sql" && sqlQueries && sqlQueries.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1, minHeight: 0 }}>
              {sqlQueries.length > 1 && (
                <div role="tablist" style={{ display: "flex", gap: 6 }}>
                  {sqlQueries.map((q, i) => (
                    <button
                      key={q.label}
                      type="button"
                      role="tab"
                      aria-selected={sqlIndex === i}
                      onClick={() => setSqlIndex(i)}
                      className="btn btn-sm"
                      style={{
                        background: sqlIndex === i ? "var(--color-teal)" : "transparent",
                        color: sqlIndex === i ? "#fff" : "var(--fg-2)",
                        border: "1px solid var(--border-default)",
                        fontWeight: sqlIndex === i ? 600 : 400,
                      }}
                    >
                      {q.label}
                    </button>
                  ))}
                </div>
              )}
              <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <SqlBlock sql={sqlQueries[sqlIndex]?.sql ?? ""} />
              </div>
            </div>
          )}
          {tab === "raw" && (
            queryResult == null ? (
              <p style={{ color: "var(--fg-3)" }}>Query is still loading.</p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                {truncated && (
                  <div style={{ padding: "8px 10px", background: "var(--bg-warning, #FFF7E5)", border: "1px solid var(--border-default)", borderRadius: 6, fontSize: 12, marginBottom: 8 }}>
                    Showing first {MAX_RENDER_ROWS.toLocaleString()} of {queryResult.rows.length.toLocaleString()} rows. Export for the full dataset.
                  </div>
                )}
                <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                  <FilterableTable
                    rows={rawRows}
                    columns={rawColumns}
                    rowKey={(_r, i) => String(i)}
                    toolbar={
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => downloadCsvRowArrays(queryResult.rows, queryResult.columns, csvFilenamePrefix, filters)}
                      >
                        <i className="ti ti-download" /> Download CSV
                      </button>
                    }
                  />
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
